import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Badge,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, Pagination, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import {
  useAffiliateDailyStats,
  useAffiliateClicks,
  useAffiliateConversions,
  formatCurrency,
  statusTone,
  type AffiliateClick,
  type AffiliateConversion,
} from '@/api/affiliate';
import { formatDate } from '@/lib/format-date';

const PAGE_SIZE = 10;

export function AffiliateAnalytics() {
  const [clicksPage, setClicksPage] = useState(1);
  const [convPage, setConvPage] = useState(1);

  const { data: daily, isLoading: dailyLoading, isError: dailyError } = useAffiliateDailyStats();
  const {
    data: clicks,
    isLoading: clicksLoading,
    isError: clicksError,
  } = useAffiliateClicks(clicksPage, PAGE_SIZE);
  const {
    data: conversions,
    isLoading: convLoading,
    isError: convError,
  } = useAffiliateConversions(convPage, PAGE_SIZE);

  const clickCols = useMemo<ColumnDef<AffiliateClick, unknown>[]>(
    () => [
      {
        id: 'clicked',
        header: 'Date',
        cell: ({ row }) => formatDate(row.original.clicked_at),
      },
      {
        id: 'source',
        header: 'Source',
        cell: ({ row }) => row.original.utm_source || row.original.referer_url || '—',
      },
      {
        id: 'campaign',
        header: 'Campaign',
        cell: ({ row }) => row.original.utm_campaign || '—',
      },
      {
        id: 'converted',
        header: 'Converted',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={row.original.converted ? 'success' : 'neutral'} appearance="subtle" size="sm">
            {row.original.converted ? 'Yes' : 'No'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const convCols = useMemo<ColumnDef<AffiliateConversion, unknown>[]>(
    () => [
      {
        id: 'date',
        header: 'Date',
        cell: ({ row }) => formatDate(row.original.converted_at),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => row.original.customer_name || `#${row.original.customer_id}`,
      },
      {
        id: 'revenue',
        header: 'Revenue',
        cell: ({ row }) => formatCurrency(row.original.total_revenue, row.original.currency),
      },
      {
        id: 'commission',
        header: 'Commission',
        cell: ({ row }) => formatCurrency(row.original.total_commission, row.original.currency),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={statusTone(row.original.commission_status)} appearance="subtle" size="sm">
            {row.original.commission_status || '—'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const hasDaily = (daily?.length ?? 0) > 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      <Card>
        <CardHeader>
          <CardTitle>Performance</CardTitle>
          <CardDescription>Clicks and conversions over time.</CardDescription>
        </CardHeader>
        <CardBody>
          {dailyLoading ? (
            <Skeleton style={{ height: 260 }} />
          ) : dailyError ? (
            <EmptyState title="Could not load performance" description="Something went wrong. Please try again." />
          ) : hasDaily ? (
            <Chart
              data={daily as unknown as Record<string, unknown>[]}
              xKey="date"
              height={260}
              series={[
                { key: 'clicks', label: 'Clicks' },
                { key: 'conversions', label: 'Conversions' },
              ]}
            />
          ) : (
            <EmptyState title="No activity yet" description="Charts appear once your link gets traffic." />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Clicks</CardTitle>
          <CardDescription>Every visit through your referral link.</CardDescription>
        </CardHeader>
        <CardBody>
          {clicksLoading ? (
            <Skeleton style={{ height: 120 }} />
          ) : clicksError ? (
            <EmptyState title="Could not load clicks" description="Something went wrong. Please try again." />
          ) : (
            <>
              <DataTable
                columns={clickCols}
                data={clicks?.data ?? []}
                getRowId={(r) => String(r.click_id)}
                emptyState="No clicks recorded yet."
              />
              {clicks && clicks.total > PAGE_SIZE && (
                <div style={{ marginTop: 'var(--vn-space-4)' }}>
                  <Pagination
                    page={clicksPage}
                    pageCount={Math.ceil(clicks.total / PAGE_SIZE)}
                    onPageChange={setClicksPage}
                    summary={`${clicks.total} clicks`}
                  />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conversions</CardTitle>
          <CardDescription>Referrals that became paying customers.</CardDescription>
        </CardHeader>
        <CardBody>
          {convLoading ? (
            <Skeleton style={{ height: 120 }} />
          ) : convError ? (
            <EmptyState title="Could not load conversions" description="Something went wrong. Please try again." />
          ) : (
            <>
              <DataTable
                columns={convCols}
                data={conversions?.data ?? []}
                getRowId={(r) =>
                  r.click_id != null
                    ? String(r.click_id)
                    : `${r.customer_id}-${r.converted_at}`
                }
                emptyState="No conversions yet."
              />
              {conversions && conversions.total > PAGE_SIZE && (
                <div style={{ marginTop: 'var(--vn-space-4)' }}>
                  <Pagination
                    page={convPage}
                    pageCount={Math.ceil(conversions.total / PAGE_SIZE)}
                    onPageChange={setConvPage}
                    summary={`${conversions.total} conversions`}
                  />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
